import { useEffect, useRef, type ReactNode } from 'react';

/**
 * Dialog konfirmasi generik — `<dialog>` bawaan browser.
 *
 * `showModal()`/`close()` memberi focus trap dan Escape-untuk-batal tanpa kode tambahan; itu
 * sebabnya elemen ini dipakai apa adanya alih-alih menulis modal sendiri. `onDismiss` menangani
 * SEMUA jalan keluar tanpa konfirmasi — tombol Batal, klik area gelap di luar kartu, dan Escape
 * (lewat event `close` bawaan) — supaya pemanggil cukup menyinkronkan satu state boolean.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel = 'Batal',
  onConfirm,
  onDismiss,
}: {
  readonly open: boolean;
  readonly title: string;
  readonly message: string;
  readonly confirmLabel: string;
  readonly cancelLabel?: string;
  readonly onConfirm: () => void;
  readonly onDismiss: () => void;
}): ReactNode {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open) dialog.showModal();
    else dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="confirm-dialog"
      onClose={onDismiss}
      onClick={(e) => {
        // `<dialog>` memenuhi viewport saat modal, jadi klik di elemen dialog ITU SENDIRI
        // (bukan anak-anaknya) berarti klik di area gelap di luar kartu.
        if (e.target === ref.current) onDismiss();
      }}
    >
      <h2>{title}</h2>
      <p className="muted small">{message}</p>
      <div className="confirm-dialog-actions">
        <button type="button" className="link" onClick={onDismiss}>
          {cancelLabel}
        </button>
        <button type="button" className="button-danger" onClick={onConfirm}>
          {confirmLabel}
        </button>
      </div>
    </dialog>
  );
}
